import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useAuthStore } from '../services/auth';
import { apiService } from '../services/api';
import { UserRole } from '../types';

interface TestModeContextType {
  isTestMode: boolean;
  toggleTestMode: () => Promise<void>;
  loading: boolean;
  /** Whether the current user is allowed to switch modes at all. */
  canToggleTestMode: boolean;
}

const TestModeContext = createContext<TestModeContextType | undefined>(undefined);

export const useTestMode = () => {
  const context = useContext(TestModeContext);
  if (context === undefined) {
    throw new Error('useTestMode must be used within a TestModeProvider');
  }
  return context;
};

interface TestModeProviderProps {
  children: ReactNode;
}

export const TestModeProvider: React.FC<TestModeProviderProps> = ({ children }) => {
  const [isTestMode, setIsTestMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user, impersonation, testModeEnabled, setTestModeEnabled } = useAuthStore();

  // System admins can switch their own session; while impersonating, the check
  // is against the admin behind the session rather than the impersonated user,
  // which the server enforces independently
  const canToggleTestMode = Boolean(
    impersonation?.isImpersonating || user?.role === UserRole.SYSTEM_ADMIN
  );

  // The server owns this flag - mirror whatever /auth/me reported, so a reload
  // or a second tab cannot show live mode while the session is really on test
  // keys. sessionStorage is kept only as the channel StripeContext listens on.
  useEffect(() => {
    const serverTestMode = testModeEnabled === true;
    const previous = sessionStorage.getItem('stripe_test_mode') === 'true';

    setIsTestMode(serverTestMode);

    if (serverTestMode) {
      sessionStorage.setItem('stripe_test_mode', 'true');
    } else {
      sessionStorage.removeItem('stripe_test_mode');
    }

    // Only announce an actual change. This provider renders inside StripeContext's
    // <Elements>, which remounts its subtree when it reloads Stripe.js - so
    // announcing unconditionally on mount makes the two providers retrigger each
    // other indefinitely.
    if (previous !== serverTestMode) {
      window.dispatchEvent(new CustomEvent('stripe-test-mode-changed'));
    }
  }, [testModeEnabled, impersonation?.isImpersonating]);

  const toggleTestMode = async () => {
    if (!canToggleTestMode) {
      console.warn('Test mode can only be toggled by a system admin');
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.toggleTestMode(!isTestMode);
      
      if (response.success) {
        // Write through the store: the effect above owns local state and
        // sessionStorage, so both stay derived from one source rather than
        // drifting if the request and the UI disagree
        setTestModeEnabled(!isTestMode);

        console.log(`🔄 Switched to ${!isTestMode ? 'TEST' : 'LIVE'} mode`);
      } else {
        throw new Error(response.error.message);
      }
    } catch (error) {
      console.error('Failed to toggle test mode:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    isTestMode,
    toggleTestMode,
    loading,
    canToggleTestMode,
  };

  return (
    <TestModeContext.Provider value={value}>
      {children}
    </TestModeContext.Provider>
  );
};
